import { useAuth } from "@/contexts/AuthContext";
import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useNavigate, Link } from "react-router-dom";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { SUBSCRIPTION_TIERS, CREDIT_PURCHASE } from "@/lib/subscription-tiers";
import { toast } from "sonner";
import { Swords, Target, Heart, Library, BarChart3, User, Loader2, Crown, Sparkles, Zap, Plus } from "lucide-react";

const calculateStreak = (sessions: { completed_at: string }[]) => {
  if (sessions.length === 0) return 0;

  const days = new Set(
    sessions.map((s) => new Date(s.completed_at).toISOString().split("T")[0])
  );

  const today = new Date().toISOString().split("T")[0];
  const yesterday = new Date(Date.now() - 86400000).toISOString().split("T")[0];

  if (!days.has(today) && !days.has(yesterday)) return 0;

  let streak = 0;
  let checkDate = new Date(days.has(today) ? today : yesterday);
  while (days.has(checkDate.toISOString().split("T")[0])) {
    streak++;
    checkDate = new Date(checkDate.getTime() - 86400000);
  }
  return streak;
};

const vizTypes = [
  {
    type: "competition" as const,
    icon: Swords,
    title: "Competition",
    description: "Prepare for game day with confidence and mental clarity.",
  },
  {
    type: "practice" as const,
    icon: Target,
    title: "Practice",
    description: "Sharpen skills and build consistency through mental reps.",
  },
  {
    type: "recovery" as const,
    icon: Heart,
    title: "Recovery",
    description: "Heal, reset, and return stronger after setbacks.",
  },
];

const Dashboard = () => {
  const { user, subscription, refreshSubscription } = useAuth();
  const navigate = useNavigate();
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [creditPurchaseLoading, setCreditPurchaseLoading] = useState(false);
  const [profile, setProfile] = useState<any>(null);
  const [stats, setStats] = useState({ sessions: 0, streak: 0, visualizations: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) return;

    const loadData = async () => {
      try {
        // Load profile
        const { data: profileData, error: profileError } = await supabase
          .from("profiles")
          .select("*, sports(name)")
          .eq("user_id", user.id)
          .maybeSingle();

        if (profileError) throw profileError;

        if (profileData && !profileData.onboarding_completed) {
          navigate("/onboarding");
          return;
        }

        setProfile(profileData);

        // Load stats
        const [{ data: sessionsData }, { count: vizCount }] = await Promise.all([
          supabase
            .from("completed_sessions")
            .select("completed_at")
            .eq("user_id", user.id),
          supabase
            .from("visualizations")
            .select("*", { count: "exact", head: true })
            .eq("user_id", user.id),
        ]);

        const allSessions = sessionsData || [];
        const streak = calculateStreak(allSessions);

        setStats({
          sessions: allSessions.length,
          streak,
          visualizations: vizCount || 0,
        });
      } catch (err: any) {
        console.error("Dashboard load error:", err);
        toast.error("Failed to load dashboard data. Please refresh.");
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [user, navigate]);

  // Refresh subscription on checkout return
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("checkout") === "success") {
      refreshSubscription();
      toast.success("Subscription activated! Welcome aboard.");
      window.history.replaceState({}, "", "/dashboard");
    }
    if (params.get("credits") === "purchased") {
      refreshSubscription();
      toast.success("Credits added! You can now create more visualizations.");
      window.history.replaceState({}, "", "/dashboard");
    }
  }, [refreshSubscription]);

  const handleCheckout = async (priceId: string) => {
    setCheckoutLoading(priceId);
    try {
      const { data, error } = await supabase.functions.invoke("create-checkout", {
        body: { priceId },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast.error("Could not start checkout. Please try again.");
    } finally {
      setCheckoutLoading(null);
    }
  };
  const handlePurchaseCredits = async () => {
    setCreditPurchaseLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("purchase-credits", {
        body: { quantity: 1 },
      });
      if (error) throw error;
      if (data?.url) window.open(data.url, "_blank");
    } catch {
      toast.error("Could not start credit purchase. Please try again.");
    } finally {
      setCreditPurchaseLoading(false);
    }
  };

  const creditsRemaining = subscription.subscribed
    ? subscription.weeklyCreditsLimit === -1
      ? null // unlimited
      : Math.max(0, subscription.weeklyCreditsLimit - subscription.weeklyCreditsUsed)
    : null;

  const isAtCreditLimit = subscription.subscribed && creditsRemaining !== null && creditsRemaining <= 0;


  if (loading) {
    return (
      <AppLayout>
        <div className="flex justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      {/* Greeting */}
        <div className="mb-10">
          <h1 className="font-display text-3xl md:text-4xl font-bold mb-2">
            Welcome back{profile?.full_name ? `, ${profile.full_name.split(" ")[0]}` : ""}
          </h1>
          <p className="text-muted-foreground text-lg">
            Ready for a mental training session?
          </p>
        </div>

        {/* Quick stats */}
        <div className="grid grid-cols-3 gap-3 sm:gap-4 mb-10 sm:mb-12">
          {[
            { label: "Sessions", value: stats.sessions },
            { label: "Streak", value: `${stats.streak} days` },
            { label: "Visualizations", value: stats.visualizations },
          ].map((stat) => (
            <div
              key={stat.label}
              className="gradient-card rounded-xl border border-border/50 p-5 text-center shadow-card"
            >
              <div className="font-display text-xl sm:text-2xl font-bold text-gradient-primary">
                {stat.value}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground mt-1">{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Subscription CTA for non-subscribers */}
        {!subscription.loading && !subscription.subscribed && (
          <div className="mb-8 gradient-card rounded-xl border border-primary/30 p-6 shadow-card">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-lg gradient-cta flex items-center justify-center flex-shrink-0">
                <Crown className="w-6 h-6 text-primary-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="font-display text-lg font-semibold mb-1">Unlock Mental Training</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Subscribe to create AI-powered visualizations. Start with a 7-day free trial.
                </p>
                <div className="flex flex-wrap gap-3">
                  <Button
                    variant="hero"
                    size="sm"
                    onClick={() => handleCheckout(SUBSCRIPTION_TIERS.essential.monthly.price_id)}
                    disabled={!!checkoutLoading}
                  >
                    {checkoutLoading === SUBSCRIPTION_TIERS.essential.monthly.price_id ? (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    ) : (
                      <Sparkles className="w-4 h-4 mr-2" />
                    )}
                    Essential — $15/mo
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleCheckout(SUBSCRIPTION_TIERS.elite.monthly.price_id)}
                    disabled={!!checkoutLoading}
                  >
                    {checkoutLoading === SUBSCRIPTION_TIERS.elite.monthly.price_id && (
                      <Loader2 className="w-4 h-4 animate-spin mr-2" />
                    )}
                    Elite — $25/mo
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Credits badge */}
        {subscription.subscribed && (
          <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center gap-3">
            <div className="flex items-center gap-2 px-4 py-2 rounded-lg border border-border/50 bg-secondary/30">
              <Zap className="w-4 h-4 text-primary" />
              <span className="text-sm font-medium">
                {creditsRemaining === null
                  ? "Unlimited credits"
                  : `${creditsRemaining} of ${subscription.weeklyCreditsLimit} credits remaining this week`}
              </span>
            </div>
            {isAtCreditLimit && (
              <Button
                size="sm"
                variant="hero"
                onClick={handlePurchaseCredits}
                disabled={creditPurchaseLoading}
              >
                {creditPurchaseLoading ? (
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                ) : (
                  <Plus className="w-4 h-4 mr-2" />
                )}
                Buy Credit — ${CREDIT_PURCHASE.price}
              </Button>
            )}
          </div>
        )}

        {/* Create new visualization */}
        <h2 className="font-display text-xl font-semibold mb-4">Start a Session</h2>
        <div className="grid md:grid-cols-3 gap-4 mb-12">
          {vizTypes.map((v) => {
            const canCreate = subscription.subscribed && !isAtCreditLimit;
            return (
              <Link
                key={v.type}
                to={canCreate ? `/create/${v.type}` : "#"}
                onClick={(e) => {
                  if (!subscription.subscribed) {
                    e.preventDefault();
                    toast.error("Subscribe to start creating visualizations.");
                  } else if (isAtCreditLimit) {
                    e.preventDefault();
                    toast.error("No credits remaining. Purchase additional credits or wait for your weekly reset.");
                  }
                }}
                className={`group gradient-card rounded-xl border border-border/50 p-6 transition-all shadow-card ${
                  canCreate ? "hover:border-primary/30" : "opacity-60 cursor-not-allowed"
                }`}
              >
                <div className="w-12 h-12 rounded-lg gradient-cta flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <v.icon className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="font-display text-lg font-semibold mb-1">{v.title}</h3>
                <p className="text-sm text-muted-foreground">{v.description}</p>
              </Link>
            );
          })}
        </div>

        {/* Quick links */}
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 sm:gap-4">
          {[
            { icon: Library, label: "Library", to: "/library" },
            { icon: BarChart3, label: "Progress", to: "/analytics" },
            { icon: User, label: "Profile", to: "/profile" },
          ].map((link) => (
            <Link
              key={link.label}
              to={link.to}
              className="flex items-center gap-3 p-4 rounded-xl border border-border/50 bg-secondary/20 hover:bg-secondary/40 transition-colors"
            >
              <link.icon className="w-5 h-5 text-primary" />
              <span className="text-sm font-medium">{link.label}</span>
            </Link>
          ))}
        </div>
    </AppLayout>
  );
};

export default Dashboard;
